import { apiClient } from './client';
import type { TildeProduct } from '@/components/primitives/products';
import type { CommercialProduct } from './catalog';

/**
 * Suscripciones de producto (modelo multi-producto): una organización contrata Empresa/Estudio/API
 * por separado (cuenta híbrida). TenantManagement, puerto 7074, scoped por `X-Tenant-Id`.
 * El backend serializa `product`/`status` como strings.
 */

export type ProductSubscriptionStatus = 'Trial' | 'Active' | 'PastDue' | 'Cancelled' | string;

export interface ProductSubscription {
  id: string;
  tenantId: string;
  /** 'Empresa' | 'Estudio' | 'Api' — espejo de TildeProduct (sin 'Admin', que es interno). */
  product: TildeProduct;
  /** Producto comercial mostrado al usuario/operador: Monotributo, Responsable Inscripto, etc. */
  commercialProduct?: CommercialProduct;
  commercialProductName?: string;
  commercialProductCategory?: string;
  tier: string;
  tierName: string;
  priceUsd: number | null;
  status: ProductSubscriptionStatus;
  startedAt: string;
  nextBillingAt: string;
  pastDueSince: string | null;
  limits: Record<string, number>;
  features: string[];
}

export interface ProductCancellationFeedbackRequest {
  reason?: string;
  reasonLabel?: string;
  note?: string;
  acceptedRecoveryOffer?: string | null;
  productName?: string;
  pageUrl?: string | null;
  browserInfo?: string | null;
}

/** GET /product-subscriptions — productos contratados por el tenant. */
export async function listProductSubscriptions(): Promise<ProductSubscription[]> {
  const { data } = await apiClient.get<ProductSubscription[]>('/product-subscriptions');
  return data;
}

/** POST /product-subscriptions/{product}/cancel — cancela en el PSP y luego localmente. */
export async function cancelProductSubscription(
  product: Exclude<TildeProduct, 'Admin'>,
  feedback?: ProductCancellationFeedbackRequest,
): Promise<ProductSubscription> {
  const { data } = await apiClient.post<ProductSubscription>(
    `/product-subscriptions/${product}/cancel`,
    feedback,
  );
  return data;
}

/** ¿La suscripción habilita el uso del producto en el instante indicado? */
export function isUsable(
  sub: ProductSubscription,
  now: Date | number = Date.now(),
): boolean {
  if (sub.status === 'Active' || sub.status === 'PastDue') return true;
  if (sub.status !== 'Trial') return false;

  const trialEndsAt = Date.parse(sub.nextBillingAt);
  const nowMs = now instanceof Date ? now.getTime() : now;
  return Number.isFinite(trialEndsAt) && Number.isFinite(nowMs) && trialEndsAt > nowMs;
}

/** Registro conocido que Billing puede mostrar para cancelar o regularizar, aun si el trial venció. */
export function isBillingManageable(sub: ProductSubscription): boolean {
  return sub.status === 'Active' || sub.status === 'PastDue' || sub.status === 'Trial';
}
